/**
 * Per-integration health verdicts for the Solution Engineer page, from what
 * the backoffice proxy can read: ITSM sync state and MDM/EDR asset freshness.
 * A read-only subset of dev-utils' integration-health-check skill, which also
 * reads data-providers poll state, CronJobs and logs the page cannot reach.
 */

export type HealthLevel = "healthy" | "warning" | "error" | "inactive" | "unknown";

export interface HealthVerdict {
  level: HealthLevel;
  label: string;
  detail?: string;
}

/** Categories with a signal the page can read. */
export const ITSM_CATEGORY = "itsm";
export const DEVICE_CATEGORIES = new Set(["mdm", "edr"]);

const HOUR_MS = 60 * 60 * 1000;
/** Prod syncs ITSM every 5 minutes; hours without one means it stopped. */
const ITSM_STALE_MS = 6 * HOUR_MS;
/** MDM/EDR collect twice a day; no device seen in 3 days points at the collection. */
const DEVICES_STALE_MS = 72 * HOUR_MS;

/** Mirrors itsm-integration-collector SyncState (the fields the verdict reads). */
export interface ItsmSyncSignal {
  status: "active" | "failed" | "paused" | "inactive";
  last_sync_at: string | null;
  consecutive_failures: number;
  error_message: string | null;
}

export function itsmVerdict(
  state: ItsmSyncSignal | null,
  now: number = Date.now(),
): HealthVerdict {
  if (state === null) {
    return { level: "warning", label: "Never synced" };
  }
  if (state.status === "failed") {
    return { level: "error", label: "Sync failing", detail: state.error_message ?? undefined };
  }
  if (state.status === "paused" || state.status === "inactive") {
    return { level: "inactive", label: `Sync ${state.status}` };
  }
  if (state.consecutive_failures > 0) {
    return {
      level: "warning",
      label: `${state.consecutive_failures} failed attempts`,
      detail: state.error_message ?? undefined,
    };
  }
  if (!state.last_sync_at) {
    return { level: "warning", label: "Never synced" };
  }
  if (now - new Date(state.last_sync_at).getTime() > ITSM_STALE_MS) {
    return { level: "warning", label: "Stale" };
  }
  return { level: "healthy", label: "Syncing" };
}

export interface DeviceSignal {
  total: number;
  latestCheckIn: string | null;
}

export function devicesVerdict(
  signal: DeviceSignal,
  now: number = Date.now(),
): HealthVerdict {
  if (signal.total === 0) {
    return { level: "warning", label: "No devices" };
  }
  if (!signal.latestCheckIn) {
    return { level: "warning", label: "No check-ins" };
  }
  if (now - new Date(signal.latestCheckIn).getTime() > DEVICES_STALE_MS) {
    return { level: "warning", label: "Stale" };
  }
  return { level: "healthy", label: "Collecting" };
}
